import { unserialize } from 'php-serialize';
import CONSTANTS from '../constants';
import GoogleHelper from '../helpers/GoogleHelper';

export default {
  getLocation,
  getPage,
  updateMenu,
  getNearByLocations,
  getChildLocations,
  getLocationSettings,
  getOutlets,
  viewableLocations,
  getDeliveryOutlet,
  fetchServerTime
};

function timeoutPromise(ms) {
  return new Promise((_, reject) =>
    setTimeout(() => reject(new Error('Request timed out')), ms)
  );
}

async function getLocation(locationSlug) {
  try {
    const timeoutDuration = 15000;

    let locationData = await Promise.race([
      fetch(`${CONSTANTS.NODE_API_URL}${CONSTANTS.URLS.LOCATIONS.GET(locationSlug)}`),
      timeoutPromise(timeoutDuration)
    ]);

    const location = await locationData.json();
    if (location && location.loc_id) {
      location.deliveryAreas = getDeliveryAreas(location);
      return location;
    } else {
      throw new Error('Location not found');
    }
  } catch (error) {
    console.error('Error:', error.message);
    throw error; // Rethrow the error to propagate it further
  }
}

async function getPage(locationSlug, pageSlug) {
  let pageData = await fetch(
    `${CONSTANTS.NODE_API_URL}${CONSTANTS.URLS.LOCATIONS.GETPAGE(locationSlug, pageSlug)}`
  );
  const page = await pageData.json();
  if (page && page.page_id) {
    return page;
  } else {
    throw new Error('page not found');
  }
}

async function updateMenu(locationSlug, data) {
  let locationData = await fetch(
    `${CONSTANTS.NODE_API_URL}${CONSTANTS.URLS.LOCATIONS.UPDATE_MENU(locationSlug)}`, {
    method: 'POST',
    body: JSON.stringify({ data }),
    headers: {
      'Content-Type': 'application/json'
    }
  }
  );
  return locationData.json();
}

async function getNearByLocations(latitude, longitude) {
  let nearbyLocations = await fetch(`${CONSTANTS.NODE_API_URL}${CONSTANTS.URLS.LOCATIONS.LIST}`, {
    method: 'POST',
    body: JSON.stringify({ latitude, longitude }),
    headers: {
      'Content-Type': 'application/json'
    }
  });
  nearbyLocations = await nearbyLocations.json();
  if (nearbyLocations) return viewableLocations(nearbyLocations);
  throw new Error('Could not get nearest locations');
}

async function getChildLocations(parentId, geoLocation) {
  let childLocations = await fetch(
    `${CONSTANTS.NODE_API_URL}${CONSTANTS.URLS.LOCATIONS.CHILDREN}/${parentId}/children`,
    {
      method: 'GET'
    }
  );
  childLocations = await childLocations.json();
  if (childLocations) return viewableLocations(childLocations, geoLocation);
  throw new Error('Could not get nearest locations');
}

async function getOutlets(parentId) {
  let childLocations = await fetch(
    `${CONSTANTS.NODE_API_URL}${CONSTANTS.URLS.LOCATIONS.CHILDREN}/${parentId}/outlets`,
    {
      method: 'GET'
    }
  );
  childLocations = await childLocations.json();
  if (childLocations) return viewableLocations(childLocations);
  throw new Error('Could not get nearest locations');
}

function viewableLocations(locations, geoLocation, diameter = null) {
  let filteredLocations = locations.filter((location) => location.location_status && location.slug);
  let newLocations = filteredLocations.map((location) => {
    let deliveryAreas = getDeliveryAreas(location);
    let distance = '';
    if (geoLocation)
      distance = GoogleHelper.distanceBetweenTwoLatLngStrings(
        geoLocation,
        `${location.latitude},${location.longitude}`
      );
    return { ...location, deliveryAreas, distance };
  });
  if (diameter !== null) {
    newLocations = newLocations.filter((loc) => loc.distance <= diameter);
  }
  newLocations.sort((a, b) => a.distance - b.distance);
  return newLocations;
}

function getDeliveryOutlet(location, outlets, currentOutlet) {
  let outlet = null;
  for (let i = 0; i < outlets.length; i++) {
    const deliveryAreas = GoogleHelper.getDeliveryArea(location, outlets[i].deliveryAreas);
    if (deliveryAreas) {
      outlet = outlets[i];
    }
    if (outlet && currentOutlet && outlet.loc_id === currentOutlet.loc_id) {
      break;
    }
  }
  return outlet;
}

function getDeliveryAreas(location) {
  if (location.options) {
    let serializedOptions = unserialize(location.options);
    if (serializedOptions.delivery_areas) {
      let deliveryAreas = [];
      Object.keys(serializedOptions.delivery_areas).forEach((areaKey) => {
        let area = serializedOptions.delivery_areas[areaKey];
        if (area.status) {
          let newArea = { ...area };
          newArea.shape = area.shape ? JSON.parse(area.shape) : null;
          newArea.circle = area.circle ? JSON.parse(area.circle) : null;
          newArea.vertices = area.vertices ? JSON.parse(area.vertices) : null;
          let charges = Object.keys(area.charge).map((key) => {
            let amount = area.charge[key].amount;
            let total = area.charge[key].total;
            if (amount)
              return {
                amount: parseFloat(amount),
                total: parseFloat(total),
                condition: area.charge[key].condition
              };
            return null;
          });
          newArea.charges = charges.filter((charge) => charge !== null);
          deliveryAreas.push(newArea);
        }
      });
      return deliveryAreas;
    }
  }
  return [];
}

async function getLocationSettings(loc_id) {
  let url = `${CONSTANTS.NODE_API_URL}${CONSTANTS.URLS.LOCATIONS.SETTINGS(loc_id)}`;
  let settings = await fetch(url, {
    method: 'GET'
  });
  settings = await settings.json();
  if (settings) return settings;

  throw new Error('Could not fetch settings');
}

async function fetchServerTime() {
  try {
    let url = `${CONSTANTS.NODE_API_URL}${CONSTANTS.URLS.SERVER.SERVER_TIME}`;
    let response = await fetch(url, {
      method: 'GET'
    });
    if (!response.ok) {
      throw new Error(`Error: ${response.status} - ${response.statusText}`);
    }
    let settings = await response.json();
    if (settings) {
      return settings;
    }
    throw new Error('Could not fetch settings');
  } catch (error) {
    console.error('Error fetching server time:', error);
    throw error;
  }
}
